import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { menusFormSchema } from './menus-form.schema';
import {
  useCreateMenuMutation,
  useGetMenuQuery,
  usePublishMenuMutation,
} from 'api/ordering-service/ordering-service';
import { useCallback, useEffect } from 'react';
import { InferType } from 'yup';
import { TransformMenuResult } from 'api/ordering-service/ordering-service.types';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router-dom';
import useMenusToast from 'pages/menu-management/menus/menus-toast/menus-toast.hooks';

export const useCreateMenuForm = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const form = useForm<TransformMenuResult | {}>({
    resolver: yupResolver(menusFormSchema),
    defaultValues: menusFormSchema.cast({}),
    context: { isEdit: false },
  });

  const [createMenu, { isLoading, isSuccess }] = useCreateMenuMutation();

  useEffect(() => {
    if (isSuccess) {
      enqueueSnackbar(
        t('page.menus.menus.create.success', { severity: 'success' }),
      );
      navigate('../native');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  const submit = async (data: TransformMenuResult) => {
    const { storeInfos, groups, startDate, ...rest } = data;
    const modified = {
      ...rest,
      startDate: DateTime.fromISO(startDate)
        .set({ hour: 0, minute: 0, second: 0 })
        .toISO({ includeOffset: false, suppressMilliseconds: true }),
      storeIds: storeInfos?.map(store => store.storeId),
      groupIds: groups.map(group => group.groupId),
      displayInfo: rest.displayInfo.map(({ languageCode, ...rest }) => ({
        langCode: languageCode,
        ...rest,
      })),
    };
    await createMenu(modified).unwrap();
  };

  return {
    form,
    isLoading,
    submit,
  };
};

export const useEditMenuForm = (catalogId: string) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const { isUninitialized, isSuccess, isLoading, isFetching, data } =
    useGetMenuQuery({ catalogId });

  const form = useForm({
    resolver: yupResolver(menusFormSchema),
    defaultValues: menusFormSchema.cast(data),
    context: { isEdit: true },
  });
  const [
    createMenu,
    { isLoading: isEditMenuLoading, isSuccess: isEditMenuSuccess },
  ] = useCreateMenuMutation();

  useEffect(() => {
    if (isEditMenuSuccess) {
      enqueueSnackbar(
        t('page.menus.menus.edit.success', { severity: 'success' }),
      );
      navigate(!!data?.externalId ? '../imported' : '../native');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMenuSuccess]);

  const submit = async (data: TransformMenuResult) => {
    const { storeInfos, groups, ...rest } = data;
    const modified = {
      ...rest,
      storeIds: storeInfos?.map(store => store.storeId),
      groupIds: groups?.map(group => group.groupId),
      displayInfo: rest.displayInfo.map(({ languageCode, ...rest }) => ({
        langCode: languageCode,
        ...rest,
      })),
    };
    await createMenu(modified).unwrap();
  };

  useEffect(() => {
    if (isSuccess) form.reset(data as InferType<typeof menusFormSchema>);
  }, [form, isSuccess, data, isFetching]);

  return {
    form,
    isLoading,
    isEditMenuLoading,
    isUninitialized,
    isFetching,
    submit,
    data,
  };
};

export const useMenuActions = (data: TransformMenuResult) => {
  const [publishMenu, { data: publishMenuData, isError: isPublishMenuError }] =
    usePublishMenuMutation();

  useMenusToast(publishMenuData?.status, data, isPublishMenuError);

  const handlePublishMenu = useCallback(async () => {
    try {
      await publishMenu({ storeIds: data?.storeIds }).unwrap();
    } catch (_e) {}
  }, [data?.storeIds, publishMenu]);

  useEffect(() => {
    const timeoutId = setInterval(() => {
      if (!isPublishMenuError && publishMenuData?.status === 'IN_PROGRESS') {
        publishMenu({
          storeIds: data?.storeIds,
          requestId: publishMenuData?.requestId,
        });
      }
    }, 5000);

    return () => clearInterval(timeoutId);
  }, [
    data?.storeIds,
    isPublishMenuError,
    publishMenu,
    publishMenuData?.requestId,
    publishMenuData?.status,
  ]);

  return { handlePublishMenu };
};
